/**
 * Board assembly v2 contract (shelf-board-assembly plan, Phase 1): additive fields on every
 * BoardAssembly, derived from its existing parts, joints, hardware, stock and construction
 * profile. Nothing here changes a part, a joint or `manufacturingHash`; saved revisions keep their
 * identity. The new information has its own identity, `assemblyHash`.
 *
 * - interfaces: one per joint, classed `contact`, `interpenetrating` or `hardware` (the
 *   compatibility table below), with a face frame on part `b` and the region the parts share.
 *   An interpenetrating joint whose nominal boxes only touch (a drawer bottom in a housed dado,
 *   a rail meeting a stile face) gets depth 0: the machining is recorded as unverified, never
 *   invented.
 * - supports: the floor and the wall as explicit nodes. Standing on the floor or on feet is
 *   declared by the geometry; a wall bracket is declared by the hardware; a piece tall enough to
 *   need anti-tip fixing (or one hanging shelves on brackets) needs the wall, and until a fixing
 *   is declared that attachment is `unresolved`.
 * - stockInputs: what the generator used to size and check the parts (thickness, longest and
 *   widest board, sheet size, allowances, the policy values), so an independent checker can
 *   recompute stock fit and support rules instead of trusting the generator's verdict.
 * - history: the fit as read, normalised and repaired, with typed changes (filled by families
 *   that adjust a fit; absent otherwise).
 *
 * Units: millimetres throughout, rounded to 0.05 mm (half a tick) so half-tick positions survive.
 * Frames are axis-aligned: v2 boards are boxes (plan: "What v2 can and cannot represent").
 */
import { z } from 'zod';
import { hashOf } from '../domain/hash';
import type { ConstructionProfile } from '../domain/profiles';
import type { AssemblyPart, Hardware, Joint, JointKind, StockUse } from './assembly';
import { manufacturingPayload, type HashableOpening } from './hash';
import { resolveStock, type AnyStock, type ResolvedStock } from './stock';
import { MOVING_GAP_MM, TOL_MM } from './validate';

export const CONTRACT_VERSION = 2 as const;

export type InterfaceClass = 'contact' | 'interpenetrating' | 'hardware';

/**
 * Compatibility table (plan Phase 1): every existing joint kind, its interface class, what the
 * nominal checker can verify from the assembly, and what stays unverified machining detail.
 */
export const JOINT_COMPATIBILITY: Readonly<Record<JointKind, { cls: InterfaceClass; verifies: string; unverified: string }>> = {
  'screwed-butt': { cls: 'contact', verifies: 'faces touch over the region', unverified: 'screw size, count and pilot holes' },
  'dowel-butt': { cls: 'contact', verifies: 'faces touch over the region', unverified: 'dowel size, count and positions' },
  'pocket-screw': { cls: 'contact', verifies: 'faces touch over the region', unverified: 'pocket positions and screw size' },
  mitre: { cls: 'contact', verifies: 'nominal boxes meet at the corner', unverified: 'the 45° cut itself (boxes are square-ended in v2)' },
  'housed-dado': { cls: 'interpenetrating', verifies: 'the entering part reaches the housing part, to the declared depth', unverified: 'housing width, depth when 0, stopped or through' },
  rebate: { cls: 'interpenetrating', verifies: 'the parts meet along the rebate line, to the declared depth', unverified: 'rebate width and depth when 0' },
  'egg-crate': { cls: 'interpenetrating', verifies: 'the crossing boards share the declared volume', unverified: 'slot widths and fit' },
  'mortise-tenon': { cls: 'interpenetrating', verifies: 'the rail reaches the stile, to the declared depth', unverified: 'tenon length, shoulders and mortise when depth is 0' },
  'through-tenon': { cls: 'interpenetrating', verifies: 'the shelf passes into the side, to the declared depth', unverified: 'tenon proportions and wedging' },
  hinge: { cls: 'hardware', verifies: 'the parts are within the hinge reach', unverified: 'hinge type, cup boring and adjustment' },
  runner: { cls: 'hardware', verifies: 'the parts are within the runner reach', unverified: 'runner length, rating and fixing positions' },
  track: { cls: 'hardware', verifies: 'the panel is within the track reach', unverified: 'track profile and fixing' },
};

const mm = z.number();
const vec = z.tuple([mm, mm, mm]);

export const InterfaceSchema = z.object({
  id: z.string().min(1),
  jointId: z.string().min(1),
  a: z.string().min(1),
  b: z.string().min(1),
  kind: z.string().min(1),
  cls: z.enum(['contact', 'interpenetrating', 'hardware']),
  /** On part `b`: origin at the region's minimum corner, `n` pointing from `b` towards `a`. Null when the parts do not meet. */
  frame: z.object({ origin: vec, u: vec, v: vec, n: vec }).nullable(),
  /** The shared rectangle in the frame (mm from the origin along u and v). */
  region: z.object({ u: mm, v: mm }).nullable(),
  /** Interpenetrating: how far `a` enters `b` (0 when the nominal boxes only touch). */
  depthMm: mm.optional(),
  /** Hardware: the running gap between the parts, and the reach allowed for this kind. */
  gapMm: mm.optional(),
  reachMm: mm.optional(),
  /**
   * How `a` meets `b` in the nominal boxes: `touch` (faces meet), `face` (enters through one face of
   * `b`), `through` (passes right through `b`), `gap` (a moving joint across its running gap).
   */
  meet: z.enum(['touch', 'face', 'through', 'gap']).optional(),
  /** Set when more than one face qualified and the documented tie-break chose (see `interfaceOf`). */
  tieBroken: z.boolean().optional(),
  /** Hardware: the attachments that serve this joint (compatible kind, on either part, at the joint's datum when it has one). */
  hardware: z.array(z.object({ kind: z.string(), partId: z.string(), at: vec })).optional(),
  toleranceMm: mm,
  provenance: z.enum(['observed', 'inferred', 'repaired']),
});
export type Interface = z.infer<typeof InterfaceSchema>;

export const AttachmentSchema = z.object({
  partId: z.string().nullable(),
  status: z.enum(['declared', 'inferred', 'unresolved']),
  via: z.string(),
});
export const SupportsSchema = z.object({
  floor: z.array(AttachmentSchema),
  wall: z.array(AttachmentSchema),
  /** The piece needs a wall fixing (tall enough for anti-tip, or brackets that hang from the wall). */
  wallRequired: z.boolean(),
});
export type Supports = z.infer<typeof SupportsSchema>;

export const StockInputSchema = z.object({
  id: z.string(),
  version: z.string(),
  kind: z.enum(['sheet', 'board', 'order']),
  thicknessMm: mm,
  maxLengthMm: mm.nullable(),
  maxWidthMm: mm.nullable(),
  sheet: z.object({ lengthMm: mm, widthMm: mm }).nullable(),
  /** Which way the grain must run on a sheet ('length' | 'width'), as the stock-fit check uses it; null for stock ordered to size. */
  grainAxis: z.enum(['length', 'width']).nullable(),
  allowRotation: z.boolean().nullable(),
});
export const StockInputsSchema = z.object({
  stocks: z.array(StockInputSchema),
  edgeAllowanceMm: mm,
  cutAllowanceMm: z.object({ l: mm, w: mm }),
  /** The rule values the generator applied (prototype policy, not furniture law). */
  policy: z.object({ id: z.string(), version: z.string(), values: z.record(z.string(), z.number()) }),
});
export type StockInputs = z.infer<typeof StockInputsSchema>;

export const HistoryChangeSchema = z.object({
  op: z.enum(['add', 'remove', 'modify']),
  /** What changed: a parameter path ("horizontals[2].xEnd") or a part id. */
  target: z.string(),
  by: z.enum(['normalise', 'repair', 'user']),
  from: z.unknown().optional(),
  to: z.unknown().optional(),
  note: z.string(),
});
export type HistoryChange = z.infer<typeof HistoryChangeSchema>;
export const HistorySchema = z.object({
  raw: z.unknown().nullable(),
  normalised: z.unknown().nullable(),
  repaired: z.unknown().nullable(),
  changes: z.array(HistoryChangeSchema),
  /** Parts removed by normalisation or repair: they cannot carry a marker in the final assembly. */
  removedParts: z.array(z.string()),
  /** The review hold (src/families/repair-history.ts): whether the repair changed the photographed design enough to need the user's acceptance. */
  hold: z.object({
    held: z.boolean(), reasons: z.array(z.string()), policy: z.string(),
    addedParts: z.array(z.string()), removedParts: z.array(z.string()), maxMoveOfWidth: z.number(),
    labels: z.record(z.string(), z.string()).optional(),
    normalised: z.array(z.string()).optional(),
  }).optional(),
});
export type History = z.infer<typeof HistorySchema>;

export const ContractSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  interfaces: z.array(InterfaceSchema),
  supports: SupportsSchema,
  stockInputs: StockInputsSchema,
  history: HistorySchema.optional(),
  /** Per-part provenance where a family knows it (absent parts: generated by family rules). */
  partProvenance: z.record(z.string(), z.enum(['observed', 'inferred', 'repaired'])).optional(),
  assemblyHash: z.string().regex(/^[0-9a-f]{64}$/),
});
export type Contract = z.infer<typeof ContractSchema>;

/** Round to 0.05 mm, normalising -0. */
const r = (v: number): number => { const x = Math.round(v * 20) / 20; return x === 0 ? 0 : x; };
const t2m = (ticks: number): number => r(ticks / 10);

interface Box { lo: [number, number, number]; hi: [number, number, number] }
const boxOf = (p: Pick<AssemblyPart, 'placement'>): Box => {
  const q = p.placement;
  return { lo: [q.x, q.y, q.z], hi: [q.x + q.sx, q.y + q.sy, q.z + q.sz] };
};
const AXES: readonly [number, number, number][] = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];

/** Hardware kinds that can serve each moving joint kind. */
const SERVES: Readonly<Record<string, RegExp>> = { hinge: /hinge/, runner: /runner|slide/, track: /track/ };

/**
 * The attachments serving a moving joint: compatible kind, on either part, at the joint's datum (y).
 * Every generated hinge bore, runner centre and track foot sits at its joint's datum; when none does,
 * the association is empty (unresolved), never borrowed from another joint's hardware.
 */
function servingHardware(j: Joint, hardware: readonly Hardware[]): NonNullable<Interface['hardware']> {
  const re = SERVES[j.kind];
  if (!re) return [];
  return hardware.filter((h) => re.test(h.kind))
    .flatMap((h) => h.at.filter((p) => (p.partId === j.a || p.partId === j.b) && Math.abs(p.y - j.datumMm) <= 0.5).map((p) => ({ kind: h.kind, partId: p.partId, at: [r(p.x), r(p.y), r(p.z)] as [number, number, number] })))
    .sort((x, y) => x.kind.localeCompare(y.kind) || x.at[1] - y.at[1] || x.at[0] - y.at[0]);
}

type AxisRel = 'apart' | 'touch' | 'inside' | 'spans' | 'enterLo' | 'enterHi';

/** How `a` sits against `b` along one axis. */
function axisRel(A: Box, B: Box, k: number): AxisRel {
  const t = TOL_MM;
  const o = Math.min(A.hi[k]!, B.hi[k]!) - Math.max(A.lo[k]!, B.lo[k]!);
  if (o < -t) return 'apart';
  if (o <= t) return 'touch';
  const below = A.lo[k]! < B.lo[k]! - t, above = A.hi[k]! > B.hi[k]! + t;
  if (below && above) return 'spans';
  if (below) return 'enterLo';
  if (above) return 'enterHi';
  return 'inside';
}

/**
 * The interface for one joint from the two parts' nominal boxes, chosen by what the joint is:
 * - contact: the one axis where the faces touch while the other two share area;
 * - interpenetrating: the face of `b` that `a` enters (its other two axes share area); a part that
 *   passes right through `b` (an egg-crate halving) meets it `through`; boxes that only touch get
 *   depth 0 (the machining is recorded as unverified, never invented);
 * - hardware: the axis of the running gap (or touch) within the kind's reach.
 * Ties (two qualifying faces, as at a corner) go to the axis along which `a` reaches furthest outside
 * `b`, then to x, y, z order, and are marked `tieBroken`. Degenerate parts and parts that do not meet
 * as the joint says get no frame.
 */
export function interfaceOf(j: Joint, a: AssemblyPart | undefined, b: AssemblyPart | undefined, hardware: readonly Hardware[]): Interface {
  const { cls } = JOINT_COMPATIBILITY[j.kind];
  const base = { id: `if:${j.id}`, jointId: j.id, a: j.a, b: j.b, kind: j.kind, cls, toleranceMm: TOL_MM, provenance: 'inferred' as const };
  const hw = cls === 'hardware' ? { hardware: servingHardware(j, hardware) } : {};
  const none = { ...base, frame: null, region: null, ...hw };
  if (!a || !b) return none;
  const A = boxOf(a), B = boxOf(b);
  if ([0, 1, 2].some((k) => !(A.hi[k]! - A.lo[k]! > TOL_MM) || !(B.hi[k]! - B.lo[k]! > TOL_MM))) return none;
  const o = [0, 1, 2].map((k) => Math.min(A.hi[k]!, B.hi[k]!) - Math.max(A.lo[k]!, B.lo[k]!));
  const rel = [0, 1, 2].map((k) => axisRel(A, B, k));
  const sharesArea = (n: number) => [0, 1, 2].filter((k) => k !== n).every((k) => o[k]! > TOL_MM);
  const outside = (k: number) => Math.max(0, B.lo[k]! - A.lo[k]!) + Math.max(0, A.hi[k]! - B.hi[k]!);
  const pick = (ks: number[]) => ks.slice().sort((x, y) => outside(y) - outside(x) || x - y);

  let cands: number[];
  let meet: NonNullable<Interface['meet']>;
  if (cls === 'contact') {
    cands = [0, 1, 2].filter((k) => rel[k] === 'touch' && sharesArea(k));
    meet = 'touch';
  } else if (cls === 'hardware') {
    const reach = MOVING_GAP_MM[j.kind] ?? 0;
    cands = [0, 1, 2].filter((k) => o[k]! <= TOL_MM && o[k]! >= -(reach + TOL_MM) && sharesArea(k));
    meet = 'gap';
  } else {
    const entries = [0, 1, 2].filter((k) => (rel[k] === 'enterLo' || rel[k] === 'enterHi') && sharesArea(k));
    const through = [0, 1, 2].filter((k) => rel[k] === 'spans' && sharesArea(k));
    const touching = [0, 1, 2].filter((k) => rel[k] === 'touch' && sharesArea(k));
    if (entries.length) { cands = entries; meet = 'face'; } else if (through.length) { cands = through; meet = 'through'; } else { cands = touching; meet = 'touch'; }
  }
  if (!cands.length) return none;
  const n = pick(cands)[0]!;
  const [ui, vi] = [0, 1, 2].filter((k) => k !== n) as [number, number];
  // which face of b: the side a enters from, the side a lies on, or (passing right through) the lower face by convention
  const loSide = rel[n] === 'enterLo' || rel[n] === 'spans' || ((rel[n] === 'touch' || rel[n] === 'apart') && (A.lo[n]! + A.hi[n]!) / 2 < (B.lo[n]! + B.hi[n]!) / 2);
  const origin: [number, number, number] = [0, 0, 0];
  origin[n] = r(loSide ? B.lo[n]! : B.hi[n]!);
  origin[ui] = r(Math.max(A.lo[ui]!, B.lo[ui]!));
  origin[vi] = r(Math.max(A.lo[vi]!, B.lo[vi]!));
  const sign = loSide ? -1 : 1;
  const nv = AXES[n]!.map((c) => (c ? c * sign : 0)) as [number, number, number];
  const frame = { origin, u: AXES[ui]!, v: AXES[vi]!, n: nv };
  const region = { u: r(o[ui]!), v: r(o[vi]!) };
  const tie = cands.length > 1 ? { tieBroken: true } : {};
  if (cls === 'interpenetrating') return { ...base, frame, region, meet, ...tie, depthMm: r(Math.max(0, o[n]!)) };
  if (cls === 'hardware') return { ...base, frame, region, meet, ...tie, gapMm: r(Math.max(0, -o[n]!)), reachMm: MOVING_GAP_MM[j.kind] ?? 0, ...hw };
  return { ...base, frame, region, meet, ...tie };
}

/** The floor and the wall as explicit nodes. */
export function supportsOf(parts: readonly AssemblyPart[], hardware: readonly Hardware[], H: number, antiTipAboveMm: number, profileRequiresWall = false): Supports {
  const feet = new Set(hardware.filter((h) => /foot$/.test(h.kind)).flatMap((h) => h.at.map((p) => p.partId)));
  const floor = parts
    .filter((p) => p.placement.y <= TOL_MM || feet.has(p.id))
    .map((p) => ({ partId: p.id, status: 'declared' as const, via: feet.has(p.id) ? 'feet' : 'stands on the floor' }));
  const brackets = hardware.filter((h) => h.kind === 'wall-bracket');
  const wall: Supports['wall'] = brackets.flatMap((h): Supports['wall'] => (h.at.length ? h.at.map((p) => ({ partId: p.partId, status: 'declared', via: 'wall-bracket' })) : [{ partId: null, status: 'declared', via: 'wall-bracket' }]));
  const hangs = hardware.some((h) => h.kind === 'cantilever-bracket');
  const wallRequired = H > antiTipAboveMm + TOL_MM || hangs || profileRequiresWall;
  if (wallRequired && !wall.length) wall.push({ partId: null, status: 'unresolved', via: hangs ? 'brackets hang shelves from one upright' : profileRequiresWall ? 'the construction profile requires a wall fixing' : 'anti-tip fixing for a tall piece' });
  return { floor, wall, wallRequired };
}

/** What the generator used: the stocks as bought, the allowances and the profile's rule values. */
/** The profile's numeric rule values (`limits`: span, cantilever ratio, minimum opening …), when it has them. */
const profileLimits = (c: ConstructionProfile): Record<string, number> => {
  const lim = (c as { limits?: Record<string, unknown> }).limits ?? {};
  return Object.fromEntries(Object.entries(lim).filter(([, v]) => typeof v === 'number' && Number.isFinite(v))) as Record<string, number>;
};
const wallFixingRequired = (c: ConstructionProfile): boolean => !!(c as { wallFixing?: { required?: boolean } }).wallFixing?.required;

/**
 * Grain and rotation rules per stock, exactly as the generators' stock-fit uses them
 * (src/families/build-common.ts `assembleBuild` / `frontsFor`, grid-bookcase/generate.ts): the carcass and the
 * back follow the back sheet's grain axis with their own rotation permission; drawer-box ply runs along
 * its length and may rotate; glass is ordered to size.
 */
function fitRules(s: StockUse, R: Pick<ResolvedStock, 'id' | 'kind' | 'allowRotation' | 'back'>): { grainAxis: 'length' | 'width' | null; allowRotation: boolean | null } {
  if (s.kind === 'order') return { grainAxis: null, allowRotation: null };
  // solid boards are cut along their length and never turned (boardFit); a sheet carcass uses the sheet rules
  if (s.id === R.id) return R.kind === 'board' ? { grainAxis: 'length', allowRotation: false } : { grainAxis: R.back.grainAxis, allowRotation: R.allowRotation };
  if (s.id === `${R.id}:back`) return { grainAxis: R.back.grainAxis, allowRotation: R.back.allowRotation };
  if (s.id === 'drawer-ply-12') return { grainAxis: 'length', allowRotation: true };
  return { grainAxis: null, allowRotation: null };
}

export function stockInputsOf(stocks: readonly StockUse[], R: Pick<ResolvedStock, 'id' | 'kind' | 'maxLengthTicks' | 'allowRotation' | 'back'>, c: ConstructionProfile, extra: Record<string, number> = {}): StockInputs {
  return {
    stocks: stocks.map((s) => ({
      id: s.id, version: s.version, kind: s.kind, thicknessMm: t2m(s.thicknessTicks),
      maxLengthMm: s.id === R.id && R.maxLengthTicks ? t2m(R.maxLengthTicks) : null,
      maxWidthMm: s.maxWidthTicks ? t2m(s.maxWidthTicks) : null,
      sheet: s.sheet ? { lengthMm: t2m(s.sheet.lengthTicks), widthMm: t2m(s.sheet.widthTicks) } : null,
      ...fitRules(s, R),
    })),
    edgeAllowanceMm: t2m(c.edgeAllowanceTicks),
    cutAllowanceMm: { l: t2m(c.cutAllowanceTicks.l), w: t2m(c.cutAllowanceTicks.w) },
    policy: {
      id: c.id, version: c.version,
      values: { maxShelfSpanMm: c.warnings.maxShelfSpanMm, antiTipAboveMm: c.warnings.antiTipAboveMm, minSupportsPerShelf: 2, ...profileLimits(c), wallFixingRequired: wallFixingRequired(c) ? 1 : 0, ...extra },
    },
  };
}

export interface ContractSource {
  familyId: string;
  familyVersion: string;
  parts: readonly AssemblyPart[];
  joints: readonly Joint[];
  hardware: readonly Hardware[];
  openings: readonly HashableOpening[];
  envelope: { W: number; H: number; D: number };
  stocks: readonly StockUse[];
  versions: Record<string, string | undefined>;
}

/** The complete identity: the manufacturing payload plus everything v2 adds and what the hash leaves out. */
export function assemblyHashOf(src: ContractSource, contract: Omit<Contract, 'assemblyHash'>): string {
  return hashOf({
    contractVersion: CONTRACT_VERSION,
    familyId: src.familyId, familyVersion: src.familyVersion, versions: src.versions,
    manufacturing: manufacturingPayload(src),
    parts: src.parts.map((p) => ({ id: p.id, stockId: p.stockId })),
    joints: src.joints,
    hardware: src.hardware,
    envelope: { W: src.envelope.W, H: src.envelope.H, D: src.envelope.D },
    stocks: src.stocks,
    contract,
  });
}

export function deriveContract(src: ContractSource, stockInputs: StockInputs, extras: Pick<Contract, 'history' | 'partProvenance'> = {}): Contract {
  const byId = new Map(src.parts.map((p) => [p.id, p]));
  const interfaces = src.joints.map((j) => interfaceOf(j, byId.get(j.a), byId.get(j.b), src.hardware));
  const supports = supportsOf(src.parts, src.hardware, src.envelope.H, stockInputs.policy.values.antiTipAboveMm ?? Infinity, stockInputs.policy.values.wallFixingRequired === 1);
  const body: Omit<Contract, 'assemblyHash'> = {
    contractVersion: CONTRACT_VERSION, interfaces, supports, stockInputs,
    ...(extras.history ? { history: extras.history } : {}),
    ...(extras.partProvenance ? { partProvenance: extras.partProvenance } : {}),
  };
  return { ...body, assemblyHash: assemblyHashOf(src, body) };
}

/** A build as the families return it: the substrate plus the stock profile and construction profile it was made with. */
export type BuildLike = ContractSource & { stock: unknown; construction: ConstructionProfile; design?: { params?: unknown } };

const cache = new WeakMap<object, { fp: string; contract: Contract }>();

/** Everything a contract is derived from; a cached contract is reused only while this is unchanged. */
const fingerprint = (b: BuildLike): string => JSON.stringify([b.familyId, b.familyVersion, b.versions, b.parts, b.joints, b.hardware, b.openings, b.envelope, b.stocks, b.stock, b.construction, b.design?.params ?? null]);

function deepFreeze<T>(v: T): T {
  if (v && typeof v === 'object' && !Object.isFrozen(v)) { Object.freeze(v); for (const x of Object.values(v as object)) deepFreeze(x); }
  return v;
}

/**
 * The v2 contract of a build, derived on demand, so generation (which runs on every edit) costs what
 * it did before. It is cached per build object but reused only while the build's content fingerprint
 * is unchanged (a mutated build gets a fresh contract), and the returned contract is frozen. History
 * and part provenance come from the design's `fitHistory` parameter when a family recorded one.
 */
export function contractOf(build: BuildLike): Contract {
  const fp = fingerprint(build);
  const hit = cache.get(build);
  if (hit && hit.fp === fp) return hit.contract;
  const R = resolveStock(build.stock as AnyStock);
  const params = (build.design?.params ?? null) as { fitHistory?: unknown; partProvenance?: unknown } | null;
  const history = params?.fitHistory ? HistorySchema.parse(params.fitHistory) : undefined;
  const partProvenance = params?.partProvenance ? ContractSchema.shape.partProvenance.parse(params.partProvenance) : undefined;
  const c = deepFreeze(deriveContract(build, stockInputsOf(build.stocks, R, build.construction), { ...(history ? { history } : {}), ...(partProvenance ? { partProvenance } : {}) }));
  cache.set(build, { fp, contract: c });
  return c;
}

/** A copy of the build with its contract attached, for anything that persists or submits an assembly. */
export function withContract<T extends BuildLike>(build: T): T & { contract: Contract } {
  return { ...build, contract: contractOf(build) };
}
